/* eslint-disable indent */
import {
  Application,
  InstallerDashboardSummary,
  InstallerProjectSummary,
  PaginatedResult,
  Product,
  Rates,
  RequiredDocument,
  RequiredDocuments,
  TrainingVideoLinks,
  WebPortalResource,
} from '../../../../../types/api';
import { RatesSchema } from '../../../../../types/schemas';
import {
  INSTALLER_ACCOUNT,
  INSTALLER_APPLICATIONS_URL,
  INSTALLER_BORROWERS_URL,
  INSTALLER_PROJECT_SUMMARY_URL,
  INSTALLER_SUMMARY_URL,
  PORTAL_RATES_URL,
  PORTAL_RESOURCES_URL,
  PORTAL_TRAINING_VIDEOS_URL,
  USER_APPLICATIONS_URL,
} from '../../constants';
import { BadRequest, CustomError, ServerError } from '../../utils/errors';
import { api } from './API';

export const getInstallerBorrowersAPI = async () => {
  try {
    const response = await api().get(INSTALLER_BORROWERS_URL);

    return response.data.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export type InstallerApplicationsParams = {
  page?: number;
  size: number;
  search?: string | null;
  status?:
    | 'IN_PROGRESS'
    | 'APPROVED'
    | 'PENDING_CHANGE_ORDER'
    | 'CLOSED'
    | null;
};
export const getPaginatedInstallerApplications = async (
  params: InstallerApplicationsParams
) => {
  try {
    const response = await api().get<PaginatedResult<Application>>(
      `${INSTALLER_APPLICATIONS_URL}`,
      {
        params,
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const getInstallerDashboardSummary = async () => {
  try {
    const response = await api().get<InstallerDashboardSummary>(
      INSTALLER_SUMMARY_URL
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const getInstallerProjectSummary = async () => {
  try {
    const response = await api().get<InstallerProjectSummary>(
      INSTALLER_PROJECT_SUMMARY_URL
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export type CloseApplicationVariables = {
  application_id: string;
  using_alternative_financing?: boolean;
  closed_reason: NonNullable<Application['closed_reason']>;
  close_requested_by: NonNullable<Application['close_requested_by']>;
  closed_sub_reason: NonNullable<Application['closed_sub_reason']>;
  closed_reason_notes: NonNullable<Application['closed_reason_notes']>;
};
export const closeApplication = async (
  vars: CloseApplicationVariables
): Promise<Application> => {
  const { application_id, ...close_details } = vars;
  try {
    const res = await api().delete<Application>(
      `${INSTALLER_APPLICATIONS_URL}${application_id}`,
      {
        data: { ...close_details, internal_status: 'Closed' },
      }
    );
    return res.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export type PatchInstallerApplicationBody = {
  project_nickname?: string | null;
  loan_amount?: number;
  product?: string;
  dealer_fee?: string | null;
  borrower_advance?: string | null;
  interest_payer_during_construction?: string | null;
  special_payment_percentage?: number | null;
  project_type?: string | null;
  loan_category?: string | null;
  borrower_business_name?: string;
  is_flagged_non_profit_by_installer?: 'Yes' | 'No';
  sys_prop_address_state?: string;
  project_timeline_estimation?: string;
  deal_description_and_context?: string | null;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_position_title?: string;
  contact_email?: string;
  contact_phone?: string;
  assets?: { type: string; name: string; price?: number }[];
};

export type PatchInstallerApplicationResponse = {
  id: string;
} & PatchInstallerApplicationBody;

export const patchInstallerApplication = async (
  applicationId: string,
  body: PatchInstallerApplicationBody
): Promise<PatchInstallerApplicationResponse> => {
  try {
    const res = await api().patch<PatchInstallerApplicationResponse>(
      `${INSTALLER_APPLICATIONS_URL}${applicationId}`,
      body
    );
    return res.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

// installer_views.py → InstallerLoanApplicationDocumentsView
export const getApplicationDocuments = async (application_id: string) => {
  try {
    const response = await api().get<{ data: RequiredDocuments }>(
      `${INSTALLER_APPLICATIONS_URL}${application_id}/documents`
    );
    return response.data.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

// installer_views.py → InstallerLoanApplicationDocumentsView
export const uploadApplicationDocument = async (
  application_id: string,
  data: FormData
) => {
  try {
    const response = await api().post<{ data: RequiredDocument }>(
      `${INSTALLER_APPLICATIONS_URL}${application_id}/documents`,
      data,
      { timeout: 10 * 60 * 1000 }
    );
    return response.data.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const uploadRailwayDocument = async (data: FormData) => {
  try {
    const response = await api().post<{ data: any }>(
      `${USER_APPLICATIONS_URL}railway-upload`,
      data,
      { timeout: 10 * 60 * 1000 }
    );
    return response.data.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

/**
 * @param {object} data - data required to create a new borrower account and an application
 * @returns {Promise<string>} - returns the id of the new application
 */
export const createBorrowerAccount = async (data) => {
  try {
    const response = await api().post<{ id: string }>(
      INSTALLER_BORROWERS_URL,
      data
    );
    return response.data.id;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const getAccount = async (accountId, data) => {
  try {
    const response = await api().get(`${INSTALLER_ACCOUNT}${accountId}/`, data);
    return response.data.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const updateAccount = async (accountId, data) => {
  try {
    const response = await api().put(`${INSTALLER_ACCOUNT}${accountId}/`, data);
    return response.data.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const listResources = async () => {
  const response = await api().get<WebPortalResource[]>(
    `${PORTAL_RESOURCES_URL}`
  );
  return response.data;
};

export const getRates = async (loanPricingDate?: string): Promise<Rates> => {
  const response = await api().get<unknown>(`${PORTAL_RATES_URL}`, {
    params: loanPricingDate
      ? { loan_pricing_date: loanPricingDate }
      : undefined,
  });
  const validatedData = RatesSchema.parse(response.data);
  return validatedData;
};

export const listTrainingVideos = async (): Promise<TrainingVideoLinks[]> => {
  const response = await api().get<TrainingVideoLinks[]>(
    `${PORTAL_TRAINING_VIDEOS_URL}`
  );
  return response.data;
};

export const getProductById = async (productId: string): Promise<Product> => {
  try {
    const response = await api().get<Product>(`products/${productId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const getProductsByIds = async (
  productIds: string[]
): Promise<Product[]> => {
  if (productIds.length === 0) return [];

  const query = new URLSearchParams({
    product_ids: productIds.join(','),
  }).toString();

  try {
    const response = await api().get<Product[]>(`products?${query}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      throw getErrorByStatusCode(status, data?.detail, data?.errors);
    } else {
      throw getErrorByStatusCode(503, undefined, undefined);
    }
  }
};

export const getApplicationAccessContacts = async (applicationId: string) => {
  const response = await api().get(
    `${INSTALLER_APPLICATIONS_URL}${applicationId}/access-contacts`
  );
  return response.data;
};

export const getExternalApplication = async (externalSourceId: string) => {
  const response = await api().get(
    `${INSTALLER_APPLICATIONS_URL}external/${externalSourceId}`
  );
  return response.data;
};

// TODO: https://sunstonecreditinc.atlassian.net/browse/SUNENG-2719
const getErrorByStatusCode = (
  status_code: number,
  displayError?: string,
  data?: Record<string, any>
) => {
  switch (status_code) {
    case 400:
      // For validation errors, preserve the actual error message
      if (data?.error) {
        return new Error(data.error);
      }
      return new BadRequest({
        code: status_code,
        message: displayError,
        data,
      } as any);

    case 409:
    case 410:
    case 422:
      return new CustomError({ code: status_code, message: displayError });
    case 423:
      // Special case SF errors
      return new ServerError({
        code: status_code,
        message: displayError,
        data,
      } as any);
    case 503:
      return new ServerError({
        code: status_code,
        message: displayError,
        data,
      } as any);

    default:
      return new ServerError({
        code: status_code,
        message: displayError,
        data,
      } as any);
  }
};
